import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { ApiError } from '../../../lib/api';
import { useStaffCompleteTraining } from '../../../hooks/useTrainingProgress';

/**
 * "Mark complete on their behalf" for an external (redirect) module the
 * learner finished off-platform but never self-certified. Two-step so a
 * mis-click in the attempt-history modal cannot complete someone's
 * training; the optional reason lands on the audit event.
 */
export function StaffCompleteSection({
  personId,
  taskId,
  onDone,
}: {
  personId: string;
  taskId: string;
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const complete = useStaffCompleteTraining();

  function submit() {
    complete.mutate(
      { personId, taskId, reason },
      {
        onSuccess: () => {
          toast.success('Marked complete on their behalf; recorded against their record');
          onDone();
        },
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not mark complete'),
      },
    );
  }

  if (!confirming) {
    return (
      <div className="mt-4 border-t border-line/10 pt-3">
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<CheckCircle2 className="h-3.5 w-3.5" />}
          onClick={() => setConfirming(true)}
        >
          Mark complete on their behalf
        </Button>
        <p className="mt-1.5 text-xs text-fg-dim">
          For when they finished the external training and told you, but did not press the
          button themselves.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2 rounded-token-sm border border-warning-soft/30 bg-warning-soft/10 p-3">
      <p className="text-sm text-fg">
        This records the training as complete with you as the person who marked it, not the
        learner. It cannot be undone from here.
      </p>
      <label className="block text-xs font-medium text-fg-muted">
        Reason (optional, kept on the audit trail)
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder="e.g. confirmed by email 12 Aug"
          className="mt-1 w-full rounded-token-sm border border-line/20 bg-surface px-2.5 py-1.5 text-sm text-fg"
        />
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={complete.isPending}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" loading={complete.isPending} onClick={submit}>
          Confirm completion
        </Button>
      </div>
    </div>
  );
}
